// Passkey sign-in (devise/sessions/new) and "Add a passkey" (Settings -> My
// Info). Server side: app/services/passkeys.rb.
//
// WebAuthn speaks ArrayBuffers; our JSON speaks base64url. The four helpers
// below convert between them, which is all @github/webauthn-json would have
// added, so there is no npm dependency. Promise chains rather than async/await
// so the pack needs no regenerator runtime.

function base64urlToBuffer(value) {
  var base64 = String(value).replace(/-/g, "+").replace(/_/g, "/");
  while (base64.length % 4) base64 += "=";
  var binary = window.atob(base64);
  var bytes = new Uint8Array(binary.length);
  for (var i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function bufferToBase64url(buffer) {
  var bytes = new Uint8Array(buffer);
  var binary = "";
  for (var i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return window.btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function descriptorsFromJSON(list) {
  return (list || []).map(function (descriptor) {
    return Object.assign({}, descriptor, { id: base64urlToBuffer(descriptor.id) });
  });
}

function credentialToJSON(credential) {
  var response = credential.response;
  var json = {
    type: credential.type,
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    authenticatorAttachment: credential.authenticatorAttachment || null,
    clientExtensionResults: credential.getClientExtensionResults ? credential.getClientExtensionResults() : {},
    response: { clientDataJSON: bufferToBase64url(response.clientDataJSON) }
  };
  if (response.attestationObject) {
    json.response.attestationObject = bufferToBase64url(response.attestationObject);
    json.response.transports = response.getTransports ? response.getTransports() : [];
  }
  if (response.authenticatorData) {
    json.response.authenticatorData = bufferToBase64url(response.authenticatorData);
    json.response.signature = bufferToBase64url(response.signature);
    json.response.userHandle = response.userHandle ? bufferToBase64url(response.userHandle) : null;
  }
  return json;
}

function passkeysSupported() {
  return !!(window.PublicKeyCredential && navigator.credentials && navigator.credentials.create && window.fetch);
}

function postJSON(url, body) {
  return requestJSON("POST", url, body);
}

function requestJSON(method, url, body) {
  var token = document.querySelector('meta[name="csrf-token"]');
  return fetch(url, {
    method: method,
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": token ? token.content : ""
    },
    body: JSON.stringify(body || {})
  }).then(function (response) {
    return response.json().catch(function () { return {}; }).then(function (data) {
      if (!response.ok) throw new Error(data.error || "Something went wrong. Please try again.");
      return data;
    });
  });
}

// The browser rejects with NotAllowedError when the person closes the passkey
// sheet or it times out. That is a choice, not a failure, so it shows nothing.
function isCancellation(error) {
  return error && (error.name === "NotAllowedError" || error.name === "AbortError");
}

function showError(element, message) {
  if (!element) return;
  element.textContent = message || "";
  element.style.display = message ? "" : "none";
}

// ---------------------------------------------------------------------------
// Sign-in page
// ---------------------------------------------------------------------------

var conditionalRequest = null; // AbortController for the autofill request

function abortConditionalRequest() {
  if (conditionalRequest) {
    conditionalRequest.abort();
    conditionalRequest = null;
  }
}

function signInWithPasskey(container, mediation) {
  var errorBox = container.querySelector("[data-passkey-error]");
  var button = container.querySelector("[data-passkey-sign-in-button]");
  var controller = window.AbortController ? new AbortController() : null;
  if (mediation === "conditional") conditionalRequest = controller;

  return postJSON(container.dataset.optionsUrl).then(function (options) {
    var publicKey = Object.assign({}, options, {
      challenge: base64urlToBuffer(options.challenge),
      allowCredentials: descriptorsFromJSON(options.allowCredentials)
    });
    var request = { publicKey: publicKey };
    if (mediation) request.mediation = mediation;
    if (controller) request.signal = controller.signal;
    return navigator.credentials.get(request);
  }).then(function (credential) {
    if (!credential) return;
    if (button) button.disabled = true;
    var remember = document.getElementById("user_remember_me");
    return postJSON(container.dataset.signInUrl, {
      credential: credentialToJSON(credential),
      remember_me: remember && remember.checked ? "1" : "0",
      device_label: deviceLabel()
    }).then(function (data) {
      window.location.assign(data.redirect_to || "/");
    });
  }).catch(function (error) {
    if (button) button.disabled = false;
    if (isCancellation(error)) return;
    showError(errorBox, error.message);
  });
}

function initSignIn() {
  var container = document.querySelector("[data-passkey-sign-in]");
  if (!container || !passkeysSupported()) return;

  container.style.display = "";
  var button = container.querySelector("[data-passkey-sign-in-button]");
  button.addEventListener("click", function () {
    // Only one WebAuthn request may be pending; the button replaces autofill.
    abortConditionalRequest();
    showError(container.querySelector("[data-passkey-error]"), "");
    signInWithPasskey(container, null);
  });

  // Passkey autofill: focusing the email field offers saved passkeys in the
  // browser's own suggestion list (the field is autocomplete="username webauthn").
  if (PublicKeyCredential.isConditionalMediationAvailable) {
    PublicKeyCredential.isConditionalMediationAvailable().then(function (available) {
      if (available) signInWithPasskey(container, "conditional");
    }).catch(function () {});
  }
}

// ---------------------------------------------------------------------------
// Settings -> My Info -> Passkeys
// ---------------------------------------------------------------------------

// Where the passkey is being added or used, e.g. "Safari on iPad", shown in
// Settings next to each passkey. iPadOS Safari presents itself as a Mac
// ("Macintosh"), so a touch screen on a "Mac" means iPad; iOS browsers other
// than Safari carry their own tokens (CriOS, FxiOS, EdgiOS).
function deviceLabel() {
  var ua = navigator.userAgent;
  var touchMac = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  var device = /iPhone/.test(ua) ? "iPhone" : (/iPad/.test(ua) || touchMac) ? "iPad" : /Android/.test(ua) ? "Android" :
    /Mac OS X|Macintosh/.test(ua) ? "Mac" : /Windows/.test(ua) ? "Windows" : /CrOS/.test(ua) ? "Chromebook" :
    /Linux/.test(ua) ? "Linux" : "this device";
  var browser = /Edg(e|iOS|A)?\//.test(ua) ? "Edge" : /Firefox\/|FxiOS\//.test(ua) ? "Firefox" :
    /Chrome\/|CriOS\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : null;
  return browser ? browser + " on " + device : device;
}

function initSettings() {
  var container = document.querySelector("[data-passkeys-settings]");
  if (!container) return;

  var errorBoxForRename = container.querySelector("[data-passkey-error]");
  Array.prototype.forEach.call(container.querySelectorAll("[data-passkey-rename]"), function (link) {
    link.addEventListener("click", function (event) {
      event.preventDefault();
      var nickname = window.prompt("Name this passkey", link.dataset.nickname || "");
      if (nickname === null) return;
      requestJSON("PATCH", link.dataset.url, { nickname: nickname }).then(function () {
        window.location.reload();
      }).catch(function (error) {
        showError(errorBoxForRename, error.message);
      });
    });
  });

  var addButton = container.querySelector("[data-passkey-add]");
  if (!addButton) return; // impersonating: management is hidden

  if (!passkeysSupported()) {
    addButton.style.display = "none";
    var unsupported = container.querySelector("[data-passkey-unsupported]");
    if (unsupported) unsupported.style.display = "";
    return;
  }

  var errorBox = container.querySelector("[data-passkey-error]");
  var busy = false;

  addButton.addEventListener("click", function (event) {
    event.preventDefault();
    if (busy) return;
    busy = true;
    showError(errorBox, "");

    postJSON(container.dataset.optionsUrl).then(function (options) {
      var publicKey = Object.assign({}, options, {
        challenge: base64urlToBuffer(options.challenge),
        user: Object.assign({}, options.user, { id: base64urlToBuffer(options.user.id) }),
        excludeCredentials: descriptorsFromJSON(options.excludeCredentials)
      });
      return navigator.credentials.create({ publicKey: publicKey });
    }).then(function (credential) {
      if (!credential) return;
      return postJSON(container.dataset.createUrl, {
        credential: credentialToJSON(credential),
        device_label: deviceLabel()
      }).then(function () {
        // Still on Settings: reload so the new passkey and the flash show.
        window.location.reload();
      });
    }).catch(function (error) {
      busy = false;
      if (error && error.name === "InvalidStateError") {
        showError(errorBox, "This device already has a passkey for your account.");
      } else if (!isCancellation(error)) {
        showError(errorBox, error.message);
      }
    });
  });
}

document.addEventListener("turbolinks:load", function () {
  initSignIn();
  initSettings();
});
// Leaving the page must not leave an autofill request pending in the browser.
document.addEventListener("turbolinks:before-visit", abortConditionalRequest);
document.addEventListener("turbolinks:before-cache", abortConditionalRequest);
